import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, FileText, Mail, Download, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API_URL } from '../config';
import { trackEvent, buildItem, waitForGtag } from '../utils/analytics';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);

  const orderRef = searchParams.get('order') || searchParams.get('ctrl');

  // Popup cadou (03.11.2026): clientul tocmai a plătit → nu-i mai arătăm popup-ul 30 de zile
  useEffect(() => {
    try { localStorage.setItem('giftPopupSeen', String(Date.now())); } catch (e) { /* ignorăm */ }
  }, []);

  useEffect(() => {
    // Simulează verificarea comenzii
    const timer = setTimeout(() => {
      setProcessing(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // GA4 purchase - O SINGURĂ DATĂ per comandă, DOAR pentru comenzi confirmate plătite,
  // cu valoarea reală din comandă (Stripe nu trimite suma în success_url). Zero date personale.
  useEffect(() => {
    if (!orderRef || !/^ORD[0-9A-Z]{8,40}$/.test(orderRef)) return;
    const sentKey = `ga_purchase_sent_${orderRef}`;
    try { if (localStorage.getItem(sentKey)) return; } catch (e) { /* localStorage indisponibil */ }

    let cancelled = false;
    (async () => {
      // Webhook-ul Stripe poate ajunge cu câteva secunde după redirect → reîncercăm până la ~30s
      let order = null;
      for (let i = 0; i < 10 && !cancelled; i++) {
        try {
          const res = await fetch(`${API_URL}/api/orders/public/${orderRef}`);
          if (res.ok) {
            const data = await res.json();
            if (data.paid) { order = data; break; }
          } else if (res.status === 404) {
            return;
          }
        } catch (e) { /* rețea - reîncercăm */ }
        await new Promise(r => setTimeout(r, 3000));
      }
      if (cancelled || !order) return;

      // gtag se încarcă doar după consimțământ; fără el NU trimitem și NU marcăm (rămâne pentru o vizită ulterioară)
      const ready = await waitForGtag(10000);
      if (cancelled || !ready) return;

      const sent = trackEvent('purchase', {
        transaction_id: order.orderNumber,
        value: Number(order.price) || 0,
        currency: 'RON',
        profit: Number(order.profit) || 0,
        ...(order.coupon ? { coupon: order.coupon } : {}),
        items: [buildItem(order.certificateType, order.price, order.variant)]
      });
      if (sent) {
        try { localStorage.setItem(sentKey, '1'); } catch (e) { /* ignorăm */ }
      }
    })();

    return () => { cancelled = true; };
  }, [orderRef]);

  if (processing) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-2xl mx-auto py-16 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Loader2 className="w-20 h-20 mx-auto text-emerald-600 animate-spin mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Se procesează plata...</h2>
            <p className="text-gray-600">Vă rugăm așteptați confirmarea tranzacției.</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      <Header />

      <div className="max-w-3xl mx-auto py-16 px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header Success */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-8 py-12 text-center">
            <CheckCircle className="w-24 h-24 mx-auto text-white mb-6" />
            <h1 className="text-4xl font-bold text-white mb-3">Plată Reușită!</h1>
            <p className="text-green-100 text-lg">Tranzacția dumneavoastră a fost procesată cu succes</p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-8">
              <h3 className="font-semibold text-blue-900 mb-3 text-lg">Număr comandă</h3>
              <p className="text-3xl font-bold text-emerald-600">{orderRef || 'N/A'}</p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <FileText className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Extrasul este în procesare</h4>
                  <p className="text-gray-600 text-sm">
                    Cererea se procesează automat pe Portalul ANCPI (Agenția Națională de Cadastru și Publicitate Imobiliară).
                    Procesul durează aproximativ <strong>5 minute</strong>.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <Mail className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Veți primi 2 email-uri de confirmare</h4>
                  <p className="text-gray-600 text-sm">
                    Când extrasul va fi gata, veți primi 2 email-uri cu:
                  </p>
                  <ul className="list-disc list-inside text-sm text-gray-600 mt-2 ml-4">
                    <li>Detalii comandă și factură fiscală</li>
                    <li>Extras emis de către ANCPI</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                <Download className="w-6 h-6 text-purple-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Procesare automată</h4>
                  <p className="text-gray-600 text-sm">
                    Sistemul nostru automatizat va descărca extrasul direct de pe Portalul ANCPI
                    și vi-l va trimite imediat ce va fi disponibil.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-200 text-center">
              <button
                onClick={() => navigate('/')}
                className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold transition-all">
                Înapoi la Pagina Principală
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                Pentru întrebări contactați-ne la: <a href="mailto:contact@cf-extras.ro" className="text-emerald-600 hover:underline">contact@cf-extras.ro</a>
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PaymentSuccess;
